import {
  FilterAction,
  FilterActions,
  FilterKeys,
  MakeFilter,
  ModelFilter,
  VehicleFilter,
  VehicleFilterMakeModel,
} from "@modules/vehicle/types/vehicleFilter";
import FilterDisclosure from "@modules/vehicle/components/FilterDisclosure";
import { useTranslation } from "next-i18next";
import { Disclosure } from "@headlessui/react";
import { ChevronUpIcon, CloseIcon, SearchIcon } from "@talladega/icons";
import classNames from "classnames";
import { Form, DivCheckbox } from "@talladega/components";
import { useState } from "react";

export interface VehicleYearFilterProps {
  makesModels: VehicleFilterMakeModel[];
  onUpdate(filterAction: FilterAction): void;
  vehicleFilters: VehicleFilter[];
}

type ModelDisplay = {
  id: string;
  name: string;
  isSelected: boolean;
};

type MakeModelDisplay = {
  name: string;
  selectedFilterCount: number;
  isSelected: boolean;
  models: ModelDisplay[];
};

const toMakeModelsDisplay = (
  makesModels: VehicleFilterMakeModel[],
  makeFilters: MakeFilter[],
  modelFilters: ModelFilter[]
) => {
  const makeModelDisplays: MakeModelDisplay[] = [];

  makesModels.forEach((make) => {
    const matchingMakeFilters = makeFilters.filter(
      (f) => f.state.make === make.name
    );
    const matchingModelFilters = modelFilters.filter(
      (f) => f.state.make === make.name
    );

    makeModelDisplays.push({
      name: make.name,
      selectedFilterCount:
        matchingMakeFilters.length + matchingModelFilters.length,
      isSelected: matchingMakeFilters.length > 0,
      models: make.models.map((model) => {
        // Determine if model is selected (filter exisits)
        const matchingToModel = matchingModelFilters.find(
          (f) => f.state.model === model.name
        );

        return {
          id: model.id,
          name: model.name,
          isSelected: matchingToModel !== undefined,
        };
      }),
    });
  });

  return makeModelDisplays;
};

const filterForSearch = (
  makeModelDisplays: MakeModelDisplay[],
  search: string
) => {
  const normalizedSearch = search.trim().toLocaleLowerCase();

  if (normalizedSearch.length === 0) {
    return makeModelDisplays;
  }

  const makesModelsFiltered: MakeModelDisplay[] = [];

  makeModelDisplays.forEach((make) => {
    // Always include selected makes
    if (make.isSelected) {
      makesModelsFiltered.push(make);
      return;
    }

    const normalizedMake = make.name.toLocaleLowerCase();
    if (normalizedSearch.length === 1) {
      // For a 1 character search, get makes that start with same letter
      if (normalizedMake.startsWith(normalizedSearch)) {
        makesModelsFiltered.push(make);
      } else {
        // Make does not start with the same letter, but include if it has selected models
        const models = make.models.filter((model) => model.isSelected);

        if (models.length > 0) {
          makesModelsFiltered.push({
            name: make.name,
            models,
            selectedFilterCount: make.selectedFilterCount,
            isSelected: make.isSelected,
          });
        }
      }
    } else {
      // For > 1 character search, do includes search on make
      if (normalizedMake.includes(normalizedSearch)) {
        makesModelsFiltered.push(make);
        return;
      }

      // Do includes search on model but always include selected models
      const models = make.models.filter(
        (model) =>
          model.name.toLocaleLowerCase().includes(normalizedSearch) ||
          model.isSelected
      );

      // Include make and matched models only
      if (models.length > 0) {
        makesModelsFiltered.push({
          name: make.name,
          models,
          selectedFilterCount: make.selectedFilterCount,
          isSelected: make.isSelected,
        });
      }
    }
  });

  return makesModelsFiltered;
};

const VehicleMakeModelFilter = ({
  onUpdate,
  vehicleFilters,
  makesModels,
}: VehicleYearFilterProps) => {
  const { t } = useTranslation(["vehiclesFilters"]);
  const [search, setSearch] = useState("");

  const makeFilters = vehicleFilters.filter(
    (vf): vf is MakeFilter => vf.key === FilterKeys.Make
  );

  const modelFilters = vehicleFilters.filter(
    (vf): vf is ModelFilter => vf.key === FilterKeys.Model
  );

  const makeModelDisplays = filterForSearch(
    toMakeModelsDisplay(makesModels, makeFilters, modelFilters),
    search
  );

  const updateMakeFilters = (
    event: React.ChangeEvent<HTMLInputElement>,
    make: string
  ) => {
    if (event.target.checked) {
      onUpdate({
        type: FilterActions.AddMakeFilter,
        payload: {
          make,
        },
      });
    } else {
      onUpdate({
        type: FilterActions.RemoveMakeFilter,
        payload: {
          make,
        },
      });
    }
  };

  const updateModelFilters = (
    event: React.ChangeEvent<HTMLInputElement>,
    make: string,
    model: string,
    id: string
  ) => {
    if (event.target.checked) {
      onUpdate({
        type: FilterActions.AddModelFilter,
        payload: {
          id,
          make,
          model,
        },
      });
    } else {
      onUpdate({
        type: FilterActions.RemoveByIdAndType,
        payload: {
          id,
          type: FilterKeys.Model,
        },
      });
    }
  };

  return (
    <FilterDisclosure
      active={makeFilters.length > 0 || modelFilters.length > 0}
      headerText={t("vehiclesFilters:make_and_model")}
      childPadding="sm"
    >
      <div className="space-y-2">
        <Form.Input
          id="searchMakeModel"
          placeholder={t("vehiclesFilters:search_make_model")}
          placeholderSize="small"
          border="thick"
          leftIcon={<SearchIcon className="text-primary-bold text-2xl" />}
          rightIcon={
            search ? (
              <CloseIcon
                className="text-slate-500 text-md cursor-pointer"
                onClick={() => setSearch("")}
              />
            ) : null
          }
          rightIconClickable
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setSearch(event.currentTarget.value)
          }
          value={search}
        />

        <div className="space-y-2" key={search}>
          {makeModelDisplays.map((make) => (
            <div
              key={make.name}
              className="rounded-lg border border-gray-50 shadow-filter"
            >
              <Disclosure defaultOpen={search.length > 1}>
                {({ open }) => (
                  <>
                    <Disclosure.Button className="flex w-full relative justify-between rounded-lg bg-white text-left pr-2 focus:outline-none focus-visible:shadow-elevation-03 py-2.5">
                      <div className="w-full focus:outline-none">
                        {make.selectedFilterCount > 0 && (
                          <span className="h-2 w-2 ml-2 rounded-full inline-block bg-secondary-salmon" />
                        )}
                        <h5
                          className={classNames(
                            "inline-block body-3 text-primary-deep",
                            {
                              "ml-4": make.selectedFilterCount === 0,
                              "ml-1.5 font-semibold":
                                make.selectedFilterCount > 0,
                            }
                          )}
                        >
                          {make.name}
                        </h5>
                      </div>

                      {make.selectedFilterCount > 0 && (
                        <div className="w-6 h-6 mr-2 flex flex-shrink-0 justify-center items-center bg-primary-light rounded-full">
                          <p className="caption-1 leading-6 text-primary-bold">
                            {make.selectedFilterCount}
                          </p>
                        </div>
                      )}
                      <ChevronUpIcon
                        className={`${
                          open
                            ? "text-primary-bold"
                            : "rotate-180 transform text-text-light-200"
                        } h-6 w-6 `}
                      />
                    </Disclosure.Button>
                    <Disclosure.Panel className="pt-0.5 pb-2.5 px-4 body-3 focus:outline-none">
                      <DivCheckbox
                        id={`shopAll${make.name}Filter`}
                        className="flex-col mb-2"
                        onChangeEvent={(event) =>
                          updateMakeFilters(event, make.name)
                        }
                        objectValue={{
                          id: 0,
                          name: {
                            english: make.name,
                            french: make.name,
                          },
                          order: 0,
                        }}
                        isChecked={make.isSelected}
                      >
                        <span>{t("vehiclesFilters:shop_all")}</span>
                      </DivCheckbox>
                      <ul className="body-3 grid grid-cols-2 gap-2   ">
                        {make.models.map((model, index) => (
                          <li key={model.id} className="grid">
                            <DivCheckbox
                              id={`shopAll${make.name}${model.name}Filter`}
                              objectValue={{
                                id: index,
                                name: {
                                  english: model.name,
                                  french: model.name,
                                },
                                order: index,
                              }}
                              onChangeEvent={(event) =>
                                updateModelFilters(
                                  event,
                                  make.name,
                                  model.name,
                                  model.id
                                )
                              }
                              isChecked={model.isSelected}
                            >
                              <span>{model.name}</span>
                            </DivCheckbox>
                          </li>
                        ))}
                      </ul>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            </div>
          ))}
        </div>
      </div>
    </FilterDisclosure>
  );
};
export default VehicleMakeModelFilter;
